import type { IntlShape } from "react-intl";
import { elementDisplayName } from "@/lib/elements";
import type { ContainedItem, FactKind } from "@/lib/games/roleplay/contract";
import type { NoticeCode } from "@/lib/games/roleplay/game-store";

// The roleplay page's own words, each written as a literal message id so the i18n checks see every one of them.
// Power names reuse the bundles' `power.name.*`. Nothing the story tells goes through
// here: authored text and narration come from agent-service only (67).

export function powerName(intl: IntlShape, power: string): string {
  switch (power) {
    case "PERSEVERANCE":
      return intl.formatMessage({ id: "power.name.Psc", defaultMessage: "Perseverance" });
    case "MINDFULNESS":
      return intl.formatMessage({ id: "power.name.Min", defaultMessage: "Mindfulness" });
    case "ADAPTION":
      return intl.formatMessage({ id: "power.name.Ada", defaultMessage: "Adaption" });
    case "CONCENTRATION":
      return intl.formatMessage({ id: "power.name.Con", defaultMessage: "Concentration" });
    case "AGGRESSION":
      return intl.formatMessage({ id: "power.name.Agg", defaultMessage: "Aggression" });
    case "INTENTION":
      return intl.formatMessage({ id: "power.name.Int", defaultMessage: "Intention" });
    case "DILIGENCE":
      return intl.formatMessage({ id: "power.name.Dil", defaultMessage: "Diligence" });
    case "STRATEGY":
      return intl.formatMessage({ id: "power.name.Str", defaultMessage: "Strategy" });
    case "RESPONSIBILITY":
      return intl.formatMessage({ id: "power.name.Rpy", defaultMessage: "Responsibility" });
    case "CHARISMA":
      return intl.formatMessage({ id: "power.name.Cha", defaultMessage: "Charisma" });
    case "COURAGE":
      return intl.formatMessage({ id: "power.name.Cou", defaultMessage: "Courage" });
    case "FAITH":
      return intl.formatMessage({ id: "power.name.Fai", defaultMessage: "Belief" });
    case "IMAGINATION":
      return intl.formatMessage({ id: "power.name.Ima", defaultMessage: "Imagination" });
    case "ABSTRACTION":
      return intl.formatMessage({ id: "power.name.Abs", defaultMessage: "Abstraction" });
    case "PASSION":
      return intl.formatMessage({ id: "power.name.Pas", defaultMessage: "Passion" });
    case "CURIOSITY":
      return intl.formatMessage({ id: "power.name.Cur", defaultMessage: "Curiosity" });
    case "INSPIRATION":
      return intl.formatMessage({ id: "power.name.Ins", defaultMessage: "Inspiration" });
    case "IMPROVISATION":
      return intl.formatMessage({ id: "power.name.Imp", defaultMessage: "Improvisation" });
    case "AUTONOMY":
      return intl.formatMessage({ id: "power.name.Aut", defaultMessage: "Autonomy" });
    case "INTUITION":
      return intl.formatMessage({ id: "power.name.Inu", defaultMessage: "Intuition" });
    case "TOLERANCE":
      return intl.formatMessage({ id: "power.name.Tol", defaultMessage: "Tolerance" });
    case "EMPATHY":
      return intl.formatMessage({ id: "power.name.Emp", defaultMessage: "Empathy" });
    case "HUMOR":
      return intl.formatMessage({ id: "power.name.Hum", defaultMessage: "Humor" });
    case "FIDELITY":
      return intl.formatMessage({ id: "power.name.Fid", defaultMessage: "Fidelity" });
    case "FAIRNESS":
      return intl.formatMessage({ id: "power.name.Fns", defaultMessage: "Fairness" });
    case "SELF_AWARENESS":
      return intl.formatMessage({ id: "power.name.Awe", defaultMessage: "Self-Perception" });
    case "PERCEPTION":
      return intl.formatMessage({ id: "power.name.Per", defaultMessage: "Perception" });
    case "LANGUAGE":
      return intl.formatMessage({ id: "power.name.Lan", defaultMessage: "Language" });
    case "EXPRESSION":
      return intl.formatMessage({ id: "power.name.Exp", defaultMessage: "Expression" });
    case "JOY":
      return intl.formatMessage({ id: "power.name.Joy", defaultMessage: "Joy" });
    default:
      // A power added in genesis after this build: its enum name, readable.
      return elementDisplayName(power);
  }
}

/** A character fact gets spark's label by its kind; two of one kind get the same label (§3.5). */
export function factLabel(intl: IntlShape, kind: FactKind): string {
  switch (kind) {
    case "name":
      return intl.formatMessage({ id: "grow.roleplay.fact.name", defaultMessage: "Name" });
    case "age":
      return intl.formatMessage({ id: "grow.roleplay.fact.age", defaultMessage: "Age" });
    case "profession":
      return intl.formatMessage({ id: "grow.roleplay.fact.profession", defaultMessage: "Profession" });
    case "hobby":
      return intl.formatMessage({ id: "grow.roleplay.fact.hobby", defaultMessage: "Hobby" });
    case "trait":
      return intl.formatMessage({ id: "grow.roleplay.fact.trait", defaultMessage: "Trait" });
    case "relationship":
      return intl.formatMessage({ id: "grow.roleplay.fact.relationship", defaultMessage: "Relationship to you" });
    case "mood":
      return intl.formatMessage({ id: "grow.roleplay.fact.mood", defaultMessage: "Mood" });
    case "unknown":
      return intl.formatMessage({ id: "grow.roleplay.fact.unknown", defaultMessage: "Detail" });
  }
}

export function categoryLabel(intl: IntlShape, category: ContainedItem["category"]): string {
  switch (category) {
    case "food":
      return intl.formatMessage({ id: "grow.roleplay.category.food", defaultMessage: "Food" });
    case "drink":
      return intl.formatMessage({ id: "grow.roleplay.category.drink", defaultMessage: "Drink" });
    case "device":
      return intl.formatMessage({ id: "grow.roleplay.category.device", defaultMessage: "Device" });
    case "tool":
      return intl.formatMessage({ id: "grow.roleplay.category.tool", defaultMessage: "Tool" });
    case "container":
      return intl.formatMessage({ id: "grow.roleplay.category.container", defaultMessage: "Container" });
    case "other":
      return intl.formatMessage({ id: "grow.roleplay.category.other", defaultMessage: "Other" });
  }
}

export function freshnessLabel(intl: IntlShape, freshness: NonNullable<ContainedItem["freshness"]>): string {
  switch (freshness) {
    case "normal":
      return intl.formatMessage({ id: "grow.roleplay.freshness.normal", defaultMessage: "fresh" });
    case "questionable":
      return intl.formatMessage({ id: "grow.roleplay.freshness.questionable", defaultMessage: "questionable" });
    case "spoiled":
      return intl.formatMessage({ id: "grow.roleplay.freshness.spoiled", defaultMessage: "spoiled" });
  }
}

/** An item's wear (78), its field `condition`; the player's own conditions are `conditionLabel`'s. */
export function wearLabel(intl: IntlShape, wear: NonNullable<ContainedItem["condition"]>): string {
  switch (wear) {
    case "good":
      return intl.formatMessage({ id: "grow.roleplay.wear.good", defaultMessage: "good" });
    case "worn":
      return intl.formatMessage({ id: "grow.roleplay.wear.worn", defaultMessage: "worn" });
    case "broken":
      return intl.formatMessage({ id: "grow.roleplay.wear.broken", defaultMessage: "broken" });
  }
}

export function conditionLabel(intl: IntlShape, condition: "food_poisoning" | "unknown"): string {
  return condition === "food_poisoning"
    ? intl.formatMessage({ id: "grow.roleplay.condition.foodPoisoning", defaultMessage: "Food poisoning" })
    : intl.formatMessage({ id: "grow.roleplay.condition.unknown", defaultMessage: "Impaired" });
}

/** The page's neutral message for a notice. */
export function noticeText(intl: IntlShape, code: NoticeCode): string {
  switch (code) {
    case "notStored":
      return intl.formatMessage({ id: "grow.roleplay.notice.notStored", defaultMessage: "Your move was not saved. Please send it again." });
    case "textRequired":
      return intl.formatMessage({ id: "grow.roleplay.notice.textRequired", defaultMessage: "A move needs your text. Write in the field how you feel, what you think and what you do." });
    case "narratorFailed":
      return intl.formatMessage({ id: "grow.roleplay.notice.narratorFailed", defaultMessage: "The story could not continue. Please try again." });
    case "narratorRefused":
      return intl.formatMessage({ id: "grow.roleplay.notice.narratorRefused", defaultMessage: "The story could not continue this way. Please describe your move a little differently." });
    case "narrationFailed":
      return intl.formatMessage({ id: "grow.roleplay.notice.narrationFailed", defaultMessage: "The story has stalled for the moment." });
    case "movedOn":
      return intl.formatMessage({ id: "grow.roleplay.notice.movedOn", defaultMessage: "The game has moved on in the meantime. Your text is still here." });
    case "checkFailed":
      return intl.formatMessage({ id: "grow.roleplay.notice.checkFailed", defaultMessage: "We could not check whether your move arrived." });
    case "notResponding":
      return intl.formatMessage({
        id: "grow.roleplay.notice.notResponding",
        defaultMessage: "The server is not responding. We could not check whether your move arrived.",
      });
    case "expired":
      return intl.formatMessage({ id: "grow.roleplay.expired", defaultMessage: "This game has expired because it was not played for too long." });
    case "notFound":
      return intl.formatMessage({ id: "grow.roleplay.notice.notFound", defaultMessage: "This game does not exist or is not yours." });
    case "closed":
      return intl.formatMessage({ id: "grow.roleplay.closed", defaultMessage: "This game is closed." });
    case "notPlayable":
      return intl.formatMessage({ id: "grow.roleplay.notice.notPlayable", defaultMessage: "This game cannot be played right now." });
    case "suspended":
      return intl.formatMessage({ id: "grow.roleplay.notice.suspended", defaultMessage: "This game is temporarily unavailable." });
    case "unavailable":
      return intl.formatMessage({ id: "grow.roleplay.notice.unavailable", defaultMessage: "The game cannot be reached right now. Please try again later." });
    case "updateNeeded":
      return intl.formatMessage({ id: "grow.roleplay.notice.updateNeeded", defaultMessage: "This page is out of date. Please reload it." });
    case "notSupported":
      return intl.formatMessage({ id: "grow.roleplay.notice.notSupported", defaultMessage: "Roleplay is not available here yet." });
    case "generic":
      return intl.formatMessage({ id: "common.serverError", defaultMessage: "Something went wrong. Please try again." });
  }
}
